import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Globe2 } from "lucide-react";
import Globe from "./Globe";
import { scrollTo } from "./Navbar";

const line = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: 0.6 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const reduced = useReducedMotion();
  const navigate = useNavigate();

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero">
      {/* Globe backdrop */}
      <motion.div
        className="absolute inset-0"
        initial={reduced ? false : { opacity: 0, scale: 1.1 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="absolute inset-0 lg:left-[30%]">
          <Globe reduced={reduced} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 lg:px-16 pt-28 pb-16">
        <div className="max-w-2xl">
          <motion.div
            custom={0} variants={line} initial="hidden" animate="show"
            className="overline text-gold mb-8 flex items-center gap-3"
          >
            <Globe2 size={14} /> International Security & Risk Advisory
          </motion.div>

          <h1 className="notranslate font-display font-light leading-[0.92] tracking-tight" translate="no">
            <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block text-6xl sm:text-7xl lg:text-8xl silver-text">
              ARCO
            </motion.span>
            <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block text-6xl sm:text-7xl lg:text-8xl gold-text">
              ORBIS2
            </motion.span>
          </h1>

          <motion.p
            custom={3} variants={line} initial="hidden" animate="show"
            className="mt-8 font-display font-light text-2xl md:text-3xl text-white/90 tracking-tight"
          >
            Security. <span className="text-silver">Risk.</span> <span className="text-gold">Resilience.</span>
          </motion.p>

          <motion.p
            custom={4} variants={line} initial="hidden" animate="show"
            className="mt-6 text-silver/70 font-body font-light text-base md:text-lg max-w-xl leading-relaxed"
          >
            Straight-talking security, risk and technical advice for people, organisations and the
            buildings they care about — from two people who've done this for real.
          </motion.p>

          <motion.div custom={5} variants={line} initial="hidden" animate="show" className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate("/services")}
              className="gold-sweep group inline-flex items-center justify-center gap-2 rounded-full bg-gold text-black px-8 py-4 text-sm font-body font-medium tracking-wide hover:bg-gold-light transition-colors duration-300"
              data-testid="hero-explore-btn"
            >
              Explore Our Services
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-silver/40 px-8 py-4 text-sm font-body tracking-wide text-silver hover:border-silver hover:text-white transition-colors duration-300"
              data-testid="hero-contact-btn"
            >
              Contact Arco Orbis2
            </button>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="overline text-silver/40 text-[0.6rem]">Scroll</span>
        <div className="h-10 w-px bg-gradient-to-b from-gold/60 to-transparent" />
      </motion.div>
    </section>
  );
}
